/**
 * Pull the Pin — end-of-day / end-of-lesson closure prompt.
 *
 * Shows all active pins with four per-pin actions: Achieved / No longer
 * needed / Let it go / Keep pinned. Auto-hides when no pins are active
 * or when the user has resolved every pin.
 *
 * Usage: <PullThePinPrompt onAllResolved={() => void} />
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Pin, Check, X, Heart, Bookmark } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ACTIONS = [
  { key: "achieved", label: "Achieved it", icon: Check, tint: "emerald" },
  { key: "not_needed", label: "No longer needed", icon: X, tint: "slate" },
  { key: "let_go", label: "Let it go", icon: Heart, tint: "rose" },
  { key: "keep", label: "Keep pinned", icon: Bookmark, tint: "indigo" },
];

const TINT = {
  emerald: "bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-900/40",
  slate: "bg-slate-50 text-slate-600 hover:bg-slate-100 border-slate-200 dark:bg-slate-800/40 dark:text-slate-300 dark:border-slate-700",
  rose: "bg-rose-50 text-rose-700 hover:bg-rose-100 border-rose-200 dark:bg-rose-900/20 dark:text-rose-300 dark:border-rose-900/40",
  indigo: "bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border-indigo-200 dark:bg-indigo-900/20 dark:text-indigo-300 dark:border-indigo-900/40",
};

export default function PullThePinPrompt({ onAllResolved }) {
  const [pins, setPins] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPins = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/pins/me`, {
        withCredentials: true,
      });
      setPins(data?.pins || []);
    } catch {
      setPins([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPins();
  }, [fetchPins]);

  const handleAction = async (pin, reason) => {
    try {
      await axios.post(
        `${API_URL}/api/pins/${pin.id}/release`,
        { reason },
        { withCredentials: true },
      );
      if (reason === "keep") {
        toast.success("Kept pinned — it'll carry forward to tomorrow");
      } else if (reason === "achieved") {
        toast.success("Nicely done");
      } else {
        toast.success("Pin pulled");
      }
      setPins((cur) => cur.filter((p) => p.id !== pin.id));
    } catch {
      toast.error("Couldn't update your pin — try again");
    }
  };

  // Fire callback when everything is resolved
  useEffect(() => {
    if (!loading && pins.length === 0) onAllResolved?.();
  }, [loading, pins.length, onAllResolved]);

  if (loading || pins.length === 0) return null;

  return (
    <div
      data-testid="pull-the-pin-prompt"
      className="bg-gradient-to-br from-slate-50 to-indigo-50 dark:from-slate-900/60 dark:to-indigo-900/20 border border-indigo-200/70 dark:border-indigo-900/50 rounded-2xl p-4 sm:p-5"
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center flex-shrink-0">
          <Pin className="w-5 h-5 text-indigo-600" />
        </div>
        <div className="flex-1">
          <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-indigo-600 dark:text-indigo-400">
            Ready to pull the pin?
          </p>
          <p className="text-sm text-slate-700 dark:text-slate-300 mt-0.5">
            How did your pin{pins.length > 1 ? "s" : ""} go today?
          </p>
        </div>
      </div>

      <div className="space-y-3">
        {pins.map((pin) => (
          <div
            key={pin.id}
            data-testid={`pin-card-${pin.id}`}
            className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-3"
          >
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                {pin.type === "goal" ? "Goal" : "Offload"}
              </span>
              {pin.carried_forward_count > 0 && (
                <span className="text-[10px] text-indigo-500 dark:text-indigo-400">
                  Carried forward ×{pin.carried_forward_count}
                </span>
              )}
            </div>
            <p className="text-sm text-slate-800 dark:text-slate-200 mb-3 break-words">
              {pin.text}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {ACTIONS.map(({ key, label, icon: Icon, tint }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => handleAction(pin, key)}
                  data-testid={`pin-release-${key}-${pin.id}`}
                  className={`inline-flex items-center justify-center gap-1.5 text-xs font-semibold rounded-lg border px-2.5 py-2 transition-colors ${TINT[tint]}`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
